import { SCOPE_DEPTH, SCOPE_OF_KIND } from '../enums'
import type {
  ConfiguratorGroupKind,
  ConfiguratorScope,
  FloorType,
  RoomType,
  ScopeLevel,
} from '../enums'

import { encodeStepKey } from './selection-key'
import {
  floorScope,
  OBJECT_SCOPE,
  roomScope,
  type SelectionScope,
} from './selection-scope'

/** What a room has to say for the tree to be resolved against it. */
export type RoomForGroups = {
  id: number
  roomType: RoomType
  sortOrder: number
  name?: string | null
  roomCode?: string | null
  /** Null while the room sits in Planning's storey-less block. Such a room is
   *  still reached by an OBJECT-level group, and by no FLOOR-level one. */
  floorId?: number | null
}

/** What a storey has to say for the tree to be resolved against it. */
export type FloorForGroups = {
  id: number
  type: FloorType
  sortOrder: number
  name?: string | null
}

/**
 * Where each kind of node may hang.
 *
 * A storey rung opens directly in a chapter, a room rung inside a storey rung,
 * a plain group anywhere. That is what makes the way the customer walks read
 * property, then storey, then room every time, and it is the only reason the
 * kinds exist.
 *
 * Stated here because both sides of the wire ask it: the CMS decides which
 * drops it draws and which kinds its row menu offers, and the server refuses
 * what arrives. Written out twice, a change to one is a drop the tree offers
 * and the API rejects.
 */
export function kindFitsUnder(
  kind: ConfiguratorGroupKind,
  /** The node it would hang in, and whether that node is a chapter. */
  parent: { kind: ConfiguratorGroupKind; isRoot: boolean } | null,
): boolean {
  if (kind === 'GROUP') return true
  if (kind === 'FLOOR') {
    return parent != null && parent.kind === 'GROUP' && parent.isRoot
  }
  return parent != null && parent.kind === 'FLOOR'
}

/**
 * Whether a rung can resolve against the tree it would hang in.
 *
 * A rung repeats against the customer's property, which a public tree has not.
 * Stated beside `kindFitsUnder` because the two together are the whole of
 * "may this node exist here", and a writer that asks only one of them enforces
 * half a rule.
 */
export const kindNeedsCustomerScope = (
  kind: ConfiguratorGroupKind,
  scope: ConfiguratorScope,
): boolean => kind !== 'GROUP' && scope !== 'CUSTOMER'

/** The shape of a tree node this module walks — the CMS tree, the customer
 *  tree and the transfer manifest all satisfy it. */
export type GroupLike<S> = {
  id: number
  /** Null on a node that only opens a rung — it is named by the storey or the
   *  room it resolves to. */
  name: string | null
  kind: ConfiguratorGroupKind
  roomTypes: readonly RoomType[]
  floorTypes: readonly FloorType[]
  sortOrder: number
  /** Where the node's own questions sit among its children. */
  questionsSortOrder: number
  questions: readonly S[]
  children: readonly GroupLike<S>[]
}

/** One place in a group's order: its own block of questions, or one child. */
type GroupEntry<A> = { own: true } | { own: false; child: A }

/** What `groupOrder` reads: a position for the own-question block, and one per
 *  child. Any tree of the configurator's shape satisfies it. */
type Arranged<C> = {
  questionsSortOrder: number
  children: readonly C[]
}

/**
 * A group's children and its own questions as the one list they are arranged
 * in.
 *
 * The questions are one step and take one place: a child group sits before or
 * after the whole block, never between two questions. Every surface that draws
 * or walks a group reads this, so the CMS list and the customer's way through
 * cannot disagree about what comes first.
 */
export function groupOrder<C extends { sortOrder: number }>(
  group: Arranged<C>,
): Array<GroupEntry<C>> {
  const entries: Array<{ at: number; child?: C }> = [
    { at: group.questionsSortOrder },
    ...group.children.map(child => ({ at: child.sortOrder, child })),
  ]
  // The block leads on a tie, which is what the `-1` default resolves to.
  return entries
    .sort((a, b) => a.at - b.at || Number(!!a.child) - Number(!!b.child))
    .map(entry =>
      entry.child ? { own: false, child: entry.child } : { own: true },
    )
}

/**
 * One place the customer stands in, and everything the chapter asks there.
 *
 * A place, not a group: a storey reached by two groups — one asking about the
 * heating, one about the staircase — is one storey the customer stands on, and
 * one step. The groups contributing to it are kept so the questions can still
 * say where they come from.
 *
 * `group` is the first contribution: for a place the customer meets as a group
 * that is the group itself, and for a storey or a room the name comes from the
 * storey or the room instead.
 */
export type GroupInstance<A> = {
  /** Every group reaching this place, in the order the step asks them. */
  contributions: A[]
  group: A
  /** The key the navigation holds the step by: the group for a place the
   *  customer meets as one, the chapter for a storey or a room — a storey
   *  reached by two groups is one step and needs one key. */
  anchorId: number
  /** The outermost group above it — the chapter the step belongs to. */
  root: A
  /** What holds this step, everywhere: its anchor and the place it stands at.
   *  A step is one row of the walk, and this is that row's identity. */
  stepKey: string
  /**
   * Every step this one sits inside, outermost first — keys of the same shape
   * `stepKey` has.
   *
   * Keys, because how far in a step is drawn is an answer only the list
   * drawing the rows can give, and `levelsOfSteps` is where it gives it. The
   * whole chain, because a list holding none of the nearest ancestors still
   * finds the next one up. A key naming a step nobody opened is simply a key
   * no list holds, so nothing here has to know which rows will be drawn.
   */
  ancestorStepKeys: readonly string[]
  floor: FloorForGroups | null
  room: RoomForGroups | null
  scope: SelectionScope
}

const roomsOfFloor = (
  rooms: readonly RoomForGroups[],
  floorId: number,
): RoomForGroups[] => rooms.filter(room => room.floorId === floorId)

/**
 * The arrangement an editor made, with the id settling a tie.
 *
 * Every list the app stores a position for is read in this order, and a tie
 * broken differently in two places is two orders for one arrangement.
 */
export const byOrder = <T extends { sortOrder: number; id: number }>(
  a: T,
  b: T,
): number => a.sortOrder - b.sortOrder || a.id - b.id

/**
 * Every place the customer meets the tree, in the order they walk it.
 *
 * One step per place within a chapter, not one per group: a group asked once
 * per storey occurs three times, and three steps all called after that group
 * leave the customer three identical rows with no way to tell which storey
 * they are answering for. Every group reaching a storey contributes its
 * questions to that storey's one step.
 *
 * A chapter reads: what it asks about the property, group by group in the order
 * they are arranged, then each storey it reaches with the rooms of that storey
 * under it. Rooms Planning has not hung on a floor trail the storeys.
 *
 * A group that repeats against something the property has not — a storey in a
 * public tree, a room type nobody has — yields no place, and its subtree goes
 * with it.
 */
export function expandGroups<A extends GroupLike<S>, S>(
  groups: readonly A[],
  floors: readonly FloorForGroups[] | null | undefined,
  rooms: readonly RoomForGroups[] | null | undefined,
): Array<GroupInstance<A>> {
  const allFloors = [...(floors ?? [])].sort(byOrder)
  const allRooms = [...(rooms ?? [])].sort(byOrder)

  const out: Array<GroupInstance<A>> = []

  for (const root of groups) {
    /** The property-level steps of this chapter, in the order arranged. */
    const own: Array<GroupInstance<A>> = []
    /**
     * What the chapter asks per storey and per room, gathered by place.
     *
     * Within one place the steps are keyed by their anchor, which merges what
     * has no name of its own and keeps apart what has: two rungs reaching one
     * storey are one step, because both would be called after that storey and
     * the customer could not tell the two rows apart — while a named group
     * inside a rung is its own step, called after itself.
     */
    const perFloor = new Map<
      number | null,
      {
        floor: FloorForGroups | null
        steps: Map<number, GroupInstance<A>>
        rooms: Map<number, Map<number, GroupInstance<A>>>
      }
    >()

    const bucketFor = (
      floor: FloorForGroups | null,
      floorId: number | null,
    ) => {
      let bucket = perFloor.get(floorId)
      if (!bucket) {
        bucket = { floor, steps: new Map(), rooms: new Map() }
        perFloor.set(floorId, bucket)
      }
      return bucket
    }

    /**
     * What a step is held by, and therefore what merges with what.
     *
     * A plain group answers for itself and is its own step. A rung is named by
     * the storey or the room it resolves to, so two rungs reaching one storey
     * are held by the chapter and come out as that storey's one step — three
     * rows all called "Erdgeschoss" say nothing about which is which.
     */
    const anchorOf = (group: A) =>
      group.kind === 'GROUP' && group.name != null ? group.id : root.id

    const open = (
      node: Node,
      place: Place,
      stepKey: string,
      ancestorStepKeys: readonly string[],
    ): GroupInstance<A> => ({
      ...place,
      contributions: [node.group],
      group: node.group,
      anchorId: node.anchor,
      root,
      stepKey,
      ancestorStepKeys,
    })

    /** Adds the group's questions to the step its anchor already opened here,
     *  or opens one. Insertion order is the order the editor arranged. */
    const contribute = (
      steps: Map<number, GroupInstance<A>>,
      node: Node,
      place: Place,
      stepKey: string,
      ancestorStepKeys: readonly string[],
    ) => {
      const held = steps.get(node.anchor)
      if (held) held.contributions.push(node.group)
      else steps.set(node.anchor, open(node, place, stepKey, ancestorStepKeys))
    }

    /**
     * Everything the walk asks of a group, worked out once.
     *
     * None of it depends on the place, and a group is walked once per place
     * the rung above it opened — twenty rooms is twenty walks of everything
     * hanging under the room rung.
     */
    type Node = {
      group: A
      /** The children and, where the group asks something, its own block —
       *  in the order the editor arranged them. A group holding no questions
       *  has no block: it puts nothing to the customer, and a step that asks
       *  nothing is one every reader of this list would have to drop again.
       *  Every chapter is exactly that. */
      arranged: Array<GroupEntry<A>>
      anchor: number
    }

    const nodes = new Map<A, Node>()
    const nodeOf = (group: A): Node => {
      const held = nodes.get(group)
      if (held) return held
      const asks = group.questions.length > 0
      const node: Node = {
        group,
        arranged: (groupOrder(group) as Array<GroupEntry<A>>).filter(
          entry => asks || !entry.own,
        ),
        anchor: anchorOf(group),
      }
      nodes.set(group, node)
      return node
    }

    /** @param chain Every step this group hangs inside, outermost first. */
    const walk = (
      group: A,
      parent: Place | null,
      chain: readonly string[],
    ): void => {
      const node = nodeOf(group)
      for (const place of placesFor(group, parent, allFloors, allRooms)) {
        const here = encodeStepKey(node.anchor, place.scope)
        // A group sharing its anchor with the node above it — an unnamed group
        // under the rung it merges into — is that step, not one inside it.
        const above =
          chain[chain.length - 1] === here ? chain.slice(0, -1) : chain
        // A chapter's step is a sibling of everything under it, not a row they
        // hang in; every other node's is a row they do.
        const below = node.group.id === root.id ? above : [...above, here]
        for (const entry of node.arranged) {
          if (!entry.own) {
            walk(entry.child, place, below)
            continue
          }
          // The group's own step takes its place among its children rather
          // than always leading them: what the editor arranged is what the
          // customer walks.
          if (place.scope.level === 'OBJECT') {
            own.push(open(node, place, here, above))
            continue
          }
          if (place.room) {
            const bucket = bucketFor(place.floor, place.room.floorId ?? null)
            let steps = bucket.rooms.get(place.room.id)
            if (!steps) {
              steps = new Map()
              bucket.rooms.set(place.room.id, steps)
            }
            contribute(steps, node, place, here, above)
            continue
          }
          contribute(
            bucketFor(place.floor, place.floor?.id ?? null).steps,
            node,
            place,
            here,
            above,
          )
        }
      }
    }

    walk(root, null, [])

    out.push(...own)
    // The storeys in the order the building stacks, each with its own rooms
    // under it; a room on no storey trails them all.
    const buckets = [
      ...allFloors.flatMap(floor => {
        const bucket = perFloor.get(floor.id)
        return bucket ? [bucket] : []
      }),
      ...(perFloor.get(null) ? [perFloor.get(null)!] : []),
    ]
    for (const bucket of buckets) {
      out.push(...bucket.steps.values())
      for (const room of allRooms) {
        const steps = bucket.rooms.get(room.id)
        if (steps) out.push(...steps.values())
      }
    }
  }

  return out
}

type Place = {
  floor: FloorForGroups | null
  room: RoomForGroups | null
  scope: SelectionScope
}

/**
 * The places one group occupies inside its parent's place.
 *
 * Nesting narrows: a child stays inside what its parent already reached, and
 * a group at the same level as its parent is that one place, dropped where its
 * own type filter excludes it.
 */
function placesFor<A extends GroupLike<S>, S>(
  group: A,
  parent: Place | null,
  floors: readonly FloorForGroups[],
  rooms: readonly RoomForGroups[],
): Place[] {
  const floorFits = (floor: FloorForGroups) =>
    group.floorTypes.length === 0 || group.floorTypes.includes(floor.type)
  const roomFits = (room: RoomForGroups) =>
    group.roomTypes.length === 0 || group.roomTypes.includes(room.roomType)

  // A plain group only gathers — it stays in the place its parent opened, so
  // questions grouped inside a storey rung are still asked once per storey.
  if (group.kind === 'GROUP') {
    return [parent ?? { floor: null, room: null, scope: OBJECT_SCOPE }]
  }

  if (group.kind === 'FLOOR') {
    // Already on a storey — the node subdivides it rather than opening a rung.
    if (parent?.floor) {
      return floorFits(parent.floor)
        ? [
            {
              floor: parent.floor,
              room: null,
              scope: floorScope(parent.floor.id),
            },
          ]
        : []
    }
    return floors.filter(floorFits).map(floor => ({
      floor,
      room: null,
      scope: floorScope(floor.id),
    }))
  }

  if (parent?.room) {
    return roomFits(parent.room)
      ? [
          {
            floor: parent.floor,
            room: parent.room,
            scope: roomScope(parent.room.id, parent.room.floorId ?? null),
          },
        ]
      : []
  }

  // A room rung reaches the rooms of the storey rung above it. Standing in no
  // storey place it reaches all of them — a ROOM node carries no storey types
  // of its own, because the FLOOR it hangs in has already narrowed them.
  const candidates = parent?.floor
    ? roomsOfFloor(rooms, parent.floor.id)
    : rooms

  return candidates.filter(roomFits).map(room => ({
    floor:
      parent?.floor ?? floors.find(floor => floor.id === room.floorId) ?? null,
    room,
    scope: roomScope(room.id, room.floorId ?? null),
  }))
}

/** One question as an instance offers it, and the group it hangs in. */
export type InstanceQuestion<A, S> = {
  question: S
  /** The group the question hangs in. A place reached by two groups asks both,
   *  so this is not the same for every question of a step. */
  group: A
}

/**
 * The questions one place puts to the customer.
 *
 * Every group reaching this place contributes its own, in the order the groups
 * are arranged. A question two of them hold is one question here.
 *
 * Nothing is inherited into this list: a question is asked where it hangs, and
 * a place answers it differently only because the same question also hangs in
 * a group reaching that place. Whether an answer was given here or comes from
 * further up is read off the stored answers, not off the tree.
 */
export function instanceQuestions<
  A extends GroupLike<S>,
  S extends { id: number },
>(instance: GroupInstance<A>): Array<InstanceQuestion<A, S>> {
  const kept = new Map<number, InstanceQuestion<A, S>>()
  for (const group of instance.contributions) {
    for (const question of group.questions) {
      const one = question as S
      if (!kept.has(one.id)) kept.set(one.id, { question: one, group })
    }
  }
  return [...kept.values()]
}

/**
 * The scopes a question is asked at, across the whole tree.
 *
 * A question that also hangs in a group reaching further down may be answered
 * differently there, and is booked there. Read off the tree, so it cannot
 * disagree with where the question actually hangs.
 */
export function questionScopes<
  A extends GroupLike<S>,
  S extends { id: number },
>(groups: readonly A[]): Map<number, Set<ScopeLevel>> {
  const scopes = new Map<number, Set<ScopeLevel>>()
  const walk = (group: A, above: ScopeLevel) => {
    const here = deeperScope(above, SCOPE_OF_KIND[group.kind])
    for (const question of group.questions) {
      const id = (question as S).id
      const held = scopes.get(id) ?? new Set<ScopeLevel>()
      held.add(here)
      scopes.set(id, held)
    }
    for (const child of group.children) walk(child as A, here)
  }
  for (const group of groups) walk(group, 'OBJECT')
  return scopes
}

/** The deeper of two scopes. `null` is a plain group, which opens none. */
const deeperScope = (a: ScopeLevel, b: ScopeLevel | null): ScopeLevel =>
  b != null && SCOPE_DEPTH[b] > SCOPE_DEPTH[a] ? b : a

/**
 * The scope a node sits in, given every node above it, outermost first.
 *
 * `kind` says what a node *opens*; a plain group opens nothing and keeps
 * whatever the rung above it opened, so the scope that counts is the deepest
 * one on the branch.
 */
export const scopeInBranch = (
  chain: readonly { kind: ConfiguratorGroupKind }[],
): ScopeLevel =>
  chain.reduce<ScopeLevel>(
    (deepest, one) => deeperScope(deepest, SCOPE_OF_KIND[one.kind]),
    'OBJECT',
  )

/**
 * Where the positions a question produces are booked.
 *
 * A plan holds positions at the property and in rooms; a storey is a place the
 * customer answers at, not one work sits at. So a question asked anywhere below
 * the property books into every room it reaches — a storey's question lands in
 * that storey's rooms, and a room that departs from the storey's answer is
 * priced with its own. Asked only for the property, it is one position there.
 */
export const booksPerRoom = (
  /** The levels the question is asked at — `questionScopes` for a whole tree,
   *  or the list one surface worked out for the one question it shows. */
  asked: Iterable<ScopeLevel> | undefined,
): boolean => {
  for (const level of asked ?? []) if (level !== 'OBJECT') return true
  return false
}

/**
 * The rooms one place covers — the rooms a position lands in when the question
 * books per room.
 *
 * A place that is a room covers itself; a storey covers its rooms; the object
 * covers all of them. A room the property no longer has covers nothing: it
 * must not fall through to the object, or one stale id would price the whole
 * building.
 */
export function roomsOfScope(
  scope: SelectionScope,
  rooms: readonly RoomForGroups[],
): RoomForGroups[] {
  if (scope.level === 'ROOM') {
    const room = rooms.find(one => one.id === scope.roomId)
    return room ? [room] : []
  }
  const covered =
    scope.level === 'OBJECT'
      ? [...rooms]
      : rooms.filter(room => room.floorId === scope.floorId)
  return covered.sort(byOrder)
}

/**
 * How far each step is indented, over the steps a list actually draws.
 *
 * A step takes the ancestors the list is holding, one level per ancestor. The
 * walk cannot answer this: a list drops a step whose every question an
 * availability rule hid, and what hung inside that step then hangs in a row
 * that is not there.
 */
export function levelsOfSteps(
  steps: readonly Pick<
    GroupInstance<unknown>,
    'stepKey' | 'ancestorStepKeys'
  >[],
): Map<string, number> {
  const drawn = new Set(steps.map(step => step.stepKey))
  const levels = new Map<string, number>()
  for (const step of steps) {
    levels.set(
      step.stepKey,
      step.ancestorStepKeys.filter(key => drawn.has(key)).length,
    )
  }
  return levels
}
